/**
 * tb2.js
 * Small, dependency-free interactions for the OS2 prototype-2 sections.
 */
(() => {
  const SELECTORS = {
    rotatingAnnouncement: '[data-tb2-announcement-rotator]',
    flipCard: '[data-tb2-flip-card]',
    slider: '[data-tb2-slider]',
    bragViewport: '[data-tb2-brag]',
  };

  function initAnnouncementRotator(root = document) {
    const el = root.querySelector(SELECTORS.rotatingAnnouncement);
    if (!el) return;

    const intervalMs = Number(el.getAttribute('data-interval-ms') || 3500);
    const items = Array.from(el.querySelectorAll('[data-tb2-announcement-item]'));
    if (items.length <= 1) return;

    let idx = items.findIndex((n) => n.getAttribute('aria-hidden') !== 'true');
    if (idx < 0) idx = 0;

    function show(nextIdx) {
      items.forEach((node, i) => {
        const active = i === nextIdx;
        node.classList.toggle('is-active', active);
        node.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
    }

    show(idx);
    window.setInterval(() => {
      idx = (idx + 1) % items.length;
      show(idx);
    }, Math.max(1200, intervalMs));
  }

  function initFlipCards(root = document) {
    const cards = root.querySelectorAll(SELECTORS.flipCard);
    if (!cards.length) return;

    cards.forEach((card) => {
      const button = card.querySelector('[data-tb2-flip-toggle]');
      const stateAttr = 'data-flipped';
      let hasInteracted = false;
      let cueTimer = null;

      function stopCue() {
        if (cueTimer) {
          window.clearInterval(cueTimer);
          cueTimer = null;
        }

        // Just stop the cue animation. No need to force layout here — there's
        // no subsequent attribute flip to register, so the original
        // `void card.offsetWidth` only caused a needless forced reflow.
        card.setAttribute('data-flip-cue', 'false');
      }

      function setFlipped(force) {
        const flipped = force ?? card.getAttribute(stateAttr) !== 'true';
        card.setAttribute(stateAttr, flipped ? 'true' : 'false');
        card.classList.toggle('is-flipped', flipped);
      }

      function toggle(force) {
        hasInteracted = true;
        stopCue();
        setFlipped(force);
      }

      // Initialize class from attribute (supports server-rendered state)
      setFlipped(card.getAttribute(stateAttr) === 'true');

      function cue() {
        if (hasInteracted || card.getAttribute(stateAttr) === 'true') return;
        card.setAttribute('data-flip-cue', 'false');
        void card.offsetWidth;
        card.setAttribute('data-flip-cue', 'true');
      }

      cue();
      cueTimer = window.setInterval(cue, 5000);
      card.addEventListener('animationend', (event) => {
        if (event.animationName === 'tb2-flip-cue') {
          card.setAttribute('data-flip-cue', 'false');
        }
      });

      if (button) {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          toggle();
        });
      } else {
        card.addEventListener('click', () => toggle());
      }

      const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      if (canHover) {
        card.addEventListener('pointerenter', () => toggle(true));
        card.addEventListener('pointerleave', () => toggle(false));
      }

      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle();
        }
      });
    });
  }

  function measureHiddenElementWidth(node, mountParent) {
    if (!(node instanceof HTMLElement)) return 0;
    const parent = mountParent instanceof HTMLElement ? mountParent : document.body;
    const ghost = node.cloneNode(true);
    ghost.setAttribute('aria-hidden', 'true');
    ghost.style.cssText = 'position:fixed;left:-10000px;top:0;visibility:visible;display:block;';
    parent.appendChild(ghost);
    const width = ghost.getBoundingClientRect().width;
    ghost.remove();
    return width;
  }

  function initBragBar(root = document) {
    const viewports = root.querySelectorAll(SELECTORS.bragViewport);
    if (!viewports.length) return;

    viewports.forEach((vp) => {
      if (!(vp instanceof HTMLElement)) return;
      const track = vp.querySelector('[data-brag-track]');
      const firstSeq = track?.querySelector('.tb2-brag__seq:not(.tb2-brag__seq--copy)');
      const sep = track?.querySelector('.tb2-brag__sep');
      if (!track || !firstSeq || !sep) return;

      // Separator is `display: none` until marquee mode kicks in, so we have
      // to clone+offscreen-measure it. Cache the result — its width depends
      // only on font + padding (both stable) so we don't need to remeasure on
      // every resize. Eliminates a forced reflow on each ResizeObserver fire.
      let cachedGap = null;

      const apply = () => {
        const avail = vp.clientWidth;
        const w = firstSeq.getBoundingClientRect().width;
        if (cachedGap === null) {
          cachedGap = measureHiddenElementWidth(sep, vp);
        }
        const gap = cachedGap;
        const loop = w + gap;

        if (w > avail + 0.5) {
          vp.setAttribute('data-mode', 'marquee');
          vp.style.setProperty('--tb2-brag-scroll', `${loop}px`);
          const seconds = Math.min(85, Math.max(12, loop / 55));
          vp.style.setProperty('--tb2-brag-duration', `${seconds}s`);
        } else {
          vp.setAttribute('data-mode', 'static');
          vp.style.removeProperty('--tb2-brag-scroll');
          vp.style.removeProperty('--tb2-brag-duration');
        }
      };

      requestAnimationFrame(() => {
        requestAnimationFrame(apply);
      });

      // Custom fonts can shift the separator's character width. If the first
      // measure happened with a fallback font, invalidate the cache once the
      // real font loads so the marquee duration stays accurate.
      if (document.fonts && typeof document.fonts.ready?.then === 'function') {
        document.fonts.ready.then(() => {
          cachedGap = null;
          apply();
        });
      }

      if (typeof ResizeObserver !== 'undefined') {
        const ro = new ResizeObserver(() => apply());
        ro.observe(vp);
        ro.observe(firstSeq);
      } else {
        window.addEventListener('resize', apply);
      }
    });
  }

  function initSliders(root = document) {
    const sliders = root.querySelectorAll(SELECTORS.slider);
    if (!sliders.length) return;

    sliders.forEach((slider) => {
      const isFadeSlider = slider.classList.contains('tb2-hero__slider');
      const wrapper = slider.closest('.tb2-final-cta__media-inner, .tb2-hero__media-inner');
      const slides = Array.from(slider.querySelectorAll('[data-tb2-slide]'));
      const buttons = wrapper ? Array.from(wrapper.querySelectorAll('[data-tb2-slide-button]')) : [];
      if (slides.length <= 1) {
        if (buttons.length) buttons.forEach((button) => button.setAttribute('hidden', 'hidden'));
        if (slides.length === 1) {
          slides[0].classList.add('is-active');
          slides[0].setAttribute('aria-hidden', 'false');
        }
        return;
      }

      let currentIndex = 0;
      let autoplayTimer = null;
      const autoplayMs = Number(slider.getAttribute('data-autoplay-ms') || 0);

      function show(index) {
        currentIndex = index;
        slides.forEach((slide, slideIndex) => {
          const active = slideIndex === index;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        });
        buttons.forEach((button, buttonIndex) => {
          const active = buttonIndex === index;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-current', active ? 'true' : 'false');
        });
        if (!isFadeSlider) {
          slider.scrollTo({
            left: slides[index].offsetLeft,
            behavior: 'smooth',
          });
        }
      }

      function stopAutoplay() {
        if (autoplayTimer) {
          window.clearInterval(autoplayTimer);
          autoplayTimer = null;
        }
      }

      function startAutoplay() {
        if (!autoplayMs) return;
        stopAutoplay();
        autoplayTimer = window.setInterval(() => {
          show((currentIndex + 1) % slides.length);
        }, Math.max(1000, autoplayMs));
      }

      const pauseRoot = wrapper || slider;

      function resumeAutoplayIfUnfocused() {
        if (!pauseRoot.matches(':focus-within')) startAutoplay();
      }

      buttons.forEach((button) => {
        button.addEventListener('click', () => {
          show(Number(button.getAttribute('data-slide-index') || 0));
          resumeAutoplayIfUnfocused();
        });
      });

      if (autoplayMs) {
        slider.addEventListener('mouseenter', stopAutoplay);
        slider.addEventListener('mouseleave', startAutoplay);
        pauseRoot.addEventListener('focusin', stopAutoplay);
        pauseRoot.addEventListener('focusout', () => {
          requestAnimationFrame(resumeAutoplayIfUnfocused);
        });
      }

      show(0);
      startAutoplay();
    });
  }

  function initAll(root = document) {
    initAnnouncementRotator(root);
    initFlipCards(root);
    initSliders(root);
    initBragBar(root);
  }

  document.addEventListener('DOMContentLoaded', () => initAll(document));

  // Theme editor compatibility: re-init on section load.
  document.addEventListener('shopify:section:load', (event) => {
    const sectionRoot = event?.target;
    if (sectionRoot instanceof HTMLElement) initAll(sectionRoot);
  });
})();

